import { useMemo } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

/** What is being put up. The page's shape follows from this and nothing else. */
export type ClimbEditorType = 'Boulder' | 'Route' | 'BoardClimb';

/** What kind of place it is going on, which is what decides the rules below. */
export type ClimbEditorPlace = 'gym' | 'crag' | 'competition' | 'board';

export interface ClimbEditorRoute {
  type: ClimbEditorType;
  place: ClimbEditorPlace;
  /** The location or board this climb belongs to, from the path. */
  locationSlug?: string;
  boardId?: string;
  /** Present when correcting a climb rather than putting a new one up. */
  climbSlug?: string;
  editing: boolean;
  /** `?variantOf=` — a board problem being re-cut at another angle. */
  variantOf: string | null;
  /**
   * `?observed=1` — the problem lit on the wall right now, which DivePeaks does
   * not have: the editor opens on the holds the device saw come on, so the
   * climber can save what they are already climbing.
   */
  fromObserved: boolean;
  /** `?climb_scan_color_id=` — the colour of a scan this climb came out of. */
  scanColorId: string | null;

  /**
   * Whether a photo is wanted at all. Indoors it is how a climb is found on a
   * wall of forty others, so it is required; on rock a line may be recorded from
   * a topo with no photo to hand, so it is not. A board brings its own.
   */
  photoRequired: boolean;
  /**
   * Whether the climb says what its holds look like. Indoors it is how one
   * climb is told from the one crossing it — "tacche arancioni", "cerchi
   * verdi". On rock there is nothing to tell apart and the model refuses one
   * outright (`hold_color_blank_for_crag`), so it is never asked for — not
   * merely optional.
   */
  usesHoldColor: boolean;
  /**
   * Whether the climb has to be named before it can go up.
   *
   * Nobody refers to a gym boulder by its name: it is the grade, the holds and
   * the sector — so the name is asked for *after* the save, with everything else
   * that is optional (see ClimbExtrasDialog). A competition problem is called by
   * name on a results sheet and a board problem is looked up by name across
   * gyms and angles, so those two are still named up front.
   */
  nameRequired: boolean;
  /** Crags order their routes along the cliff, which is what `position` is. */
  usesPosition: boolean;
}

/**
 * Which climb the editor is on, read off the URL.
 *
 * The type is the path's own answer — `/boulders/…`, `/routes/…`, a board's
 * `/climbs/…` — rather than a control on the page: a setter arrives from the
 * place they are setting, and being asked again what they came to do is a
 * question with only one right answer.
 *
 * The three rules underneath it come from the *place*, and they are the whole of
 * what separates a gym's climbs from a crag's on this page.
 */
export function useClimbEditorRoute(): ClimbEditorRoute {
  const params = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const place: ClimbEditorPlace = params.boardId
      ? 'board'
      : pathname.startsWith('/crags/')
        ? 'crag'
        : pathname.startsWith('/competitions/')
          ? 'competition'
          : 'gym';

    const type: ClimbEditorType =
      place === 'board' ? 'BoardClimb' : pathname.includes('/routes/') ? 'Route' : 'Boulder';

    return {
      type,
      place,
      locationSlug: params.gymSlug,
      boardId: params.boardId,
      climbSlug: params.climbSlug,
      editing: !!params.climbSlug,
      variantOf: searchParams.get('variantOf'),
      fromObserved: searchParams.get('observed') === '1',
      scanColorId: searchParams.get('climb_scan_color_id'),
      photoRequired: place === 'gym' || place === 'competition',
      usesHoldColor: place === 'gym' || place === 'competition',
      usesPosition: place === 'crag',
      nameRequired: place === 'competition' || place === 'board',
    };
  }, [params.boardId, params.gymSlug, params.climbSlug, pathname, searchParams]);
}
